import { create } from 'zustand';
import type { LeaderboardEntry, LeaderboardWindow } from '../models/types';
import { api } from '../services/api';
import { Logger } from '../services/logger';
import { usePlayer } from './player';

const log = new Logger('Leaderboard');

export interface LeaderboardData {
  me: LeaderboardEntry;
  /** Best first; always contains the signed-in player. */
  entries: LeaderboardEntry[];
  upCursor: string | null;
  downCursor: string | null;
  totalPlayers: number;
  loadingUp: boolean;
  loadingDown: boolean;
  /** Bumped every time the window is reloaded, so the screen can re-centre. */
  generation: number;
}

const fromWindow = (w: LeaderboardWindow, generation: number): LeaderboardData => ({
  me: w.me,
  entries: w.entries,
  upCursor: w.upCursor,
  downCursor: w.downCursor,
  totalPlayers: w.totalPlayers,
  loadingUp: false,
  loadingDown: false,
  generation,
});

export const meIndex = (d: LeaderboardData): number => Math.max(0, d.entries.findIndex((e) => e.isMe));

interface LeaderboardState {
  data: LeaderboardData | null;
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  refresh: () => Promise<void>;
  loadUp: () => Promise<void>;
  loadDown: () => Promise<void>;
  reset: () => void;
}

let generation = 0;

export const useLeaderboard = create<LeaderboardState>((set, get) => {
  async function loadMore(up: boolean) {
    const current = get().data;
    if (!current) return;
    const cursor = up ? current.upCursor : current.downCursor;
    const busy = up ? current.loadingUp : current.loadingDown;
    if (cursor == null || busy) return;
    set({ data: { ...current, ...(up ? { loadingUp: true } : { loadingDown: true }) } });
    const gen = current.generation;
    try {
      const page = await api.getLeaderboardPage(cursor, { up });
      const latest = get().data;
      // A refresh landed while this page was in flight; the page belongs to the old window.
      if (!latest || latest.generation !== gen) return;
      const known = new Set(latest.entries.map((e) => e.playerId));
      const fresh = page.entries.filter((e) => !known.has(e.playerId));
      set({
        data: {
          ...latest,
          entries: up ? [...fresh, ...latest.entries] : [...latest.entries, ...fresh],
          ...(up ? { upCursor: page.cursor, loadingUp: false } : { downCursor: page.cursor, loadingDown: false }),
        },
      });
    } catch (e) {
      log.error(`load ${up ? 'up' : 'down'} failed`, { error: e });
      const latest = get().data;
      if (latest) set({ data: { ...latest, ...(up ? { loadingUp: false } : { loadingDown: false }) } });
    }
  }

  return {
    data: null,
    loading: false,
    error: null,
    load: async () => {
      set({ loading: true, error: null });
      try {
        set({ data: fromWindow(await api.getLeaderboard(), generation), loading: false });
      } catch (e) {
        log.error('load failed', { error: e });
        set({ loading: false, error: e instanceof Error ? e.message : String(e) });
      }
    },
    refresh: async () => {
      try {
        set({ data: fromWindow(await api.getLeaderboard(), ++generation), error: null });
      } catch (e) {
        log.error('refresh failed', { error: e });
        if (!get().data) set({ error: e instanceof Error ? e.message : String(e) });
      }
    },
    loadUp: () => loadMore(true),
    loadDown: () => loadMore(false),
    reset: () => set({ data: null, loading: false, error: null }),
  };
});

// The whole window is reloaded whenever the player's own pickup count changes.
let lastPickups: number | undefined;
usePlayer.subscribe((s) => {
  const next = s.player?.profile.stats.pickups;
  const prev = lastPickups;
  lastPickups = next;
  if (prev != null && next != null && prev !== next && useLeaderboard.getState().data) void useLeaderboard.getState().refresh();
});
